import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { AppDownloadService } from '@ftr/api-core'
import { API_CONFIGURATION } from '@ftr/api-shared'
import { BadgeColors, Color, isInDesktopApp, unwrapData } from '@ftr/foundation'
import { featureFlag } from '@ftr/ui-feature-flags'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { HELP_CENTER_NAV_TITLE } from '../constants'
import { HelpSubMenuItem } from '../item/help-sub-menu-flyout-item.component'

export const SUPPORT_PORTAL_LINK = 'https://ftrgroup.service-now.com/customer'

function getHelpSubMenuItems(
  desktopAppDownloadUrl: string,
  isFtrForumEnabled: boolean,
  hasCourtSystems: boolean,
  publicPortalUrl?: string,
): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [
    {
      title: HELP_CENTER_NAV_TITLE,
      description: 'Access knowledge base articles for FTR Justice Cloud',
      icon: 'HelpCircleSolid',
      iconColor: BadgeColors.Grape,
      badgeColor: Color.Grape10,
      visible: true,
      isElevio: true,
    },
    {
      title: 'Support Portal',
      description: 'Submit or track a support request, or search the FTR Gold knowledge base',
      icon: 'AuditLog',
      iconColor: BadgeColors.Primary,
      badgeColor: Color.Primary10,
      link: SUPPORT_PORTAL_LINK,
      visible: true,
    },
    {
      title: 'Product Updates',
      description: 'The latest release notes for FTR Justice Cloud and patches for FTR Gold',
      icon: 'ErrorCircleSolid',
      iconColor: BadgeColors.Mint,
      badgeColor: Color.Mint10,
      link: 'https://fortherecord.releasenotes.io',
      visible: true,
    },
    {
      title: 'System Status',
      description: 'Check the status of FTR Justice Cloud and subscribe to incident updates',
      icon: 'CheckmarkCircle',
      iconColor: BadgeColors.Apple,
      badgeColor: Color.Apple10,
      link: 'https://fortherecord.statuspage.io',
      visible: true,
    },
    {
      title: 'Trust Center',
      description: "Public access to FTR Justice Cloud's compliance and security posture",
      icon: 'Verify',
      iconColor: BadgeColors.Lemon,
      badgeColor: Color.Lemon10,
      link: 'https://trust.fortherecord.com',
      visible: true,
    },
  ]

  if (!isInDesktopApp()) {
    items.push({
      title: 'Desktop Apps',
      description: 'Download the latest FTR Justice Cloud apps for performance and connectivity',
      icon: 'Remote',
      iconColor: isFtrForumEnabled ? BadgeColors.Mandarin : BadgeColors.Watermelon,
      badgeColor: isFtrForumEnabled ? Color.Mandarin10 : Color.Watermelon10,
      link: desktopAppDownloadUrl,
      visible: true,
    })
  }

  if (isFtrForumEnabled && hasCourtSystems) {
    items.push({
      title: 'Community Forum',
      description: 'The home for discussion about all For The Record products',
      icon: 'Forum',
      iconColor: BadgeColors.Watermelon,
      badgeColor: Color.Watermelon10,
      link: 'https://fortherecord.discourse.group/signup',
      visible: true,
    })
  }

  if (publicPortalUrl) {
    items.push({
      title: 'Public Portal',
      description: 'Request transcripts or recordings.',
      icon: 'PeopleLine',
      iconColor: BadgeColors.Blueberry,
      badgeColor: Color.Blueberry10,
      link: publicPortalUrl,
      visible: true,
    })
  }

  return items
}

function getPublicHelpSubMenuItems(desktopAppDownloadUrl: string, isFtrForumEnabled: boolean): HelpSubMenuItem[] {
  const items: HelpSubMenuItem[] = [
    {
      title: HELP_CENTER_NAV_TITLE,
      description: 'Access knowledge base articles for FTR Justice Cloud',
      icon: 'HelpCircleSolid',
      iconColor: BadgeColors.Grape,
      badgeColor: Color.Grape10,
      visible: true,
      isElevio: true,
      elevioClass: 'elevio__help-public',
    },
    {
      title: 'Product Updates',
      description: 'The latest release notes for FTR Justice Cloud and patches for FTR Gold',
      icon: 'ErrorCircleSolid',
      iconColor: BadgeColors.Mint,
      badgeColor: Color.Mint10,
      link: 'https://fortherecord.releasenotes.io',
      visible: true,
    },
    {
      title: 'Trust Center',
      description: "Public access to FTR Justice Cloud's compliance and security posture",
      icon: 'Verify',
      iconColor: BadgeColors.Lemon,
      badgeColor: Color.Lemon10,
      link: 'https://trust.fortherecord.com',
      visible: true,
    },
  ]
  if (!isInDesktopApp()) {
    items.push({
      title: 'Desktop Apps',
      description: 'Download the latest FTR Justice Cloud apps for performance and connectivity',
      icon: 'Remote',
      iconColor: isFtrForumEnabled ? BadgeColors.Mandarin : BadgeColors.Watermelon,
      badgeColor: isFtrForumEnabled ? Color.Mandarin10 : Color.Watermelon10,
      link: desktopAppDownloadUrl,
      visible: true,
    })
  }

  return items
}

@Component({
  selector: 'ftr-help-sub-menu-flyout-card',
  templateUrl: './help-sub-menu-flyout-card.component.html',
  styleUrl: './help-sub-menu-flyout-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HelpSubMenuFlyoutCardComponent {
  private readonly store = inject(Store)
  private readonly appDownloadService = inject(AppDownloadService)
  private readonly configurationService = inject(API_CONFIGURATION)

  private readonly isPublicNavigationEnabled = featureFlag('public-navigation')
  private readonly isFtrForumEnabled = featureFlag('ftr-forum')

  private readonly isLoggedIn = toSignal(this.store.select(UserState.isLoggedIn), { initialValue: false })

  readonly courtSystems = toSignal(this.store.select(UserState.courtSystems).pipe(unwrapData()), { initialValue: [] })

  readonly hasCourtSystems = computed(() => this.courtSystems().length > 0)

  readonly publicPortalUrl = this.configurationService.publicAppUrl

  readonly items = computed(() => {
    const desktopAppDownloadUrl = this.appDownloadService.getStandaloneAppLink()
    const isAnonymousWithPublicNav = this.isPublicNavigationEnabled() && !this.isLoggedIn()
    const withPublicPortalUrl = this.publicPortalUrl && this.isPublicNavigationEnabled()
    return isAnonymousWithPublicNav
      ? getPublicHelpSubMenuItems(desktopAppDownloadUrl, this.isFtrForumEnabled())
      : getHelpSubMenuItems(
          desktopAppDownloadUrl,
          this.isFtrForumEnabled(),
          this.hasCourtSystems(),
          withPublicPortalUrl ? this.publicPortalUrl : undefined,
        )
  })
}
